import React, {
  FunctionComponent,
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  SlideIn,
  ScreenReaderUpdateText,
  SaveEmailSubmitButtonGroup,
} from 'components';
import {
  SegmentedControlOptions,
  SegmentedControlQuestion,
  UpdateValueQuestion,
} from 'scenes/common/components/Questions';
import { getApiData, hasData } from 'scenes/common/helpers/apiData';
import { MarketAgreedValue, MotorFullPageProps } from 'scenes/motor/types';
import {
  getPreselectedPrice,
  getSelectedCoverType,
} from 'scenes/motor/selectors';

import HelpTextModal from './HelpTextModal/HelpTextModal';
import ScreenReaderMarketOrAgreedUpdate from './ScreenReaderMarketOrAgreedUpdate';

const MARKET_OR_AGREED_VALUE = 'marketOrAgreedValue';
const AGREED_VALUE = 'agreedValue';

const MARKET_OR_AGREED_VALUE_QUESTION =
  'Insure the car for Market or Agreed Value?';

const INSURANCE_VALUE_OPTIONS: SegmentedControlOptions<
  MarketAgreedValue,
  MarketAgreedValue
> = [
  { label: MarketAgreedValue.Market, value: MarketAgreedValue.Market },
  { label: MarketAgreedValue.Agreed, value: MarketAgreedValue.Agreed },
];

const PRICE_FORMAT_REGEX = /(\d)(?=(\d\d\d)+(?!\d))/g;

const MarketAgreedValueStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
  onRequestVehicleValue,
  onResetToMarketValue,
  onClickSaveQuoteModal,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const [showModal, setShowModal] = useState(false);

  const {
    userSelections: { marketOrAgreedValue, agreedVehicleValue },
    agreedVehicleValueRange,
    vehicleMarketValue,
  } = getApiData(funnelState.apiResponses.quoteDetails);

  const watchedAgreedValueOption = watch(
    MARKET_OR_AGREED_VALUE,
    marketOrAgreedValue
  );

  const displayHelpText = `You can enter an Agreed value between $${agreedVehicleValueRange.lowerLimit.replace(
    PRICE_FORMAT_REGEX,
    '$1,'
  )} and $${agreedVehicleValueRange.upperLimit.replace(
    PRICE_FORMAT_REGEX,
    '$1,'
  )}.`;

  const agreedVehicleValueDefault =
    agreedVehicleValue === vehicleMarketValue ? '' : agreedVehicleValue;

  useEffect(() => {
    setValue(
      MARKET_OR_AGREED_VALUE,
      getApiData(funnelState.apiResponses.quoteDetails).userSelections
        .marketOrAgreedValue
    );
    trigger();
  }, [trigger, setValue, funnelState.apiResponses.quoteDetails]);

  const handleValueTypeChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>, onChange: Function) => {
      onChange(evt);
      if (watchedAgreedValueOption === MarketAgreedValue.Agreed) {
        onResetToMarketValue();
      }
    },
    [onResetToMarketValue, watchedAgreedValueOption]
  );

  const onSubmit = useCallback(() => {
    onNext({});
  }, [onNext]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ScreenReaderUpdateText />
      <ScreenReaderMarketOrAgreedUpdate
        coverType={getSelectedCoverType(funnelState)}
        marketOrAgreedValue={watchedAgreedValueOption}
        agreedVehicleValue={agreedVehicleValue}
        price={getPreselectedPrice(funnelState)}
      />
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        helpLink={{
          id: 'what-does-this-mean-link',
          text: 'What does this mean?',
          ariaLabel: 'What does this mean? Opens dialog',
          onClick: () => setShowModal(true),
        }}
        validationRules={{
          required: true,
          validate: {
            oneOf: (value) =>
              value === MarketAgreedValue.Market ||
              value === MarketAgreedValue.Agreed,
          },
        }}
        options={INSURANCE_VALUE_OPTIONS}
        stateKey={MARKET_OR_AGREED_VALUE}
        question={MARKET_OR_AGREED_VALUE_QUESTION}
        defaultValue={watchedAgreedValueOption}
        changeHandler={handleValueTypeChange}
      />
      {watchedAgreedValueOption === MarketAgreedValue.Agreed && (
        <SlideIn>
          <UpdateValueQuestion
            errors={errors}
            control={control}
            question="Please enter the Agreed Value"
            stateKey={AGREED_VALUE}
            defaultValue={agreedVehicleValueDefault ?? ''}
            validationRules={{
              validate: {
                validField: (value) =>
                  getValues(MARKET_OR_AGREED_VALUE) ===
                    MarketAgreedValue.Market ||
                  (parseInt(value, 10) >=
                    parseInt(agreedVehicleValueRange.lowerLimit, 10) &&
                    parseInt(value, 10) <=
                      parseInt(agreedVehicleValueRange.upperLimit, 10)),
                hasUpdated: (value) =>
                  getValues(MARKET_OR_AGREED_VALUE) ===
                    MarketAgreedValue.Market ||
                  (hasData(funnelState.apiResponses.quoteDetails) &&
                    agreedVehicleValue === value),
              },
            }}
            helpText={displayHelpText}
            onUpdateClick={() => {
              onRequestVehicleValue(getValues(AGREED_VALUE));
            }}
          />
        </SlideIn>
      )}
      <SaveEmailSubmitButtonGroup
        onClickSaveQuoteModal={onClickSaveQuoteModal}
        submitEnabled={hooksForm.isValid}
      />
      <HelpTextModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </form>
  );
};

export default MarketAgreedValueStep;
